import { useTranslation } from "react-i18next"
import { useMessageData } from "@/components/chat/useMessageData"
import { memo } from "react"
import { Message as ArchivedMessage } from "@/reducers/messagesSlice"
import MessageHelpers from "@/lib/messageHelpers"
import { areMessageFromDifferentDay } from "@/lib/messageDate"
import { Message } from "@/components/ui/message/message"
import { MessageBody } from "@/components/ui/message/message-body"
import { MessageText } from "@/components/ui/message/message-text"
import DayMarker from "@/components/DayMarker"

function SearchHistoryMessage({
  message,
  previous,
  next,
  highlighted,
}: {
  message: ArchivedMessage
  previous?: ArchivedMessage
  next?: ArchivedMessage
  highlighted: boolean
}) {
  const { t } = useTranslation()
  const data = useMessageData(message)
  const grouped = MessageHelpers.shouldGroup(
    message,
    previous,
    data.accountChat,
  )
  const showAvatar = !MessageHelpers.shouldGroup(
    message,
    next,
    data.accountChat,
  )

  return (
    <div
      className="mx-auto w-full max-w-3xl"
      data-history-message-id={message.id}
    >
      {(!previous || areMessageFromDifferentDay(message, previous)) && (
        <DayMarker date={new Date(message.timestamp)} />
      )}
      <Message
        message={message}
        interactive={false}
        variant={data.fromSlickAI ? "ai" : data.variant}
        showAvatar={showAvatar}
        grouped={grouped}
        className={
          highlighted
            ? "rounded-lg bg-blue-100/50 py-2 dark:bg-blue-900/30"
            : undefined
        }
      >
        <Message.Avatar
          showWhenReadOnly
          src={data.fromSlickAI ? "/slickAvatar.svg" : data.avatarSrc}
          name={data.authorName}
          fallback={data.authorInitials}
          avatarColor={data.avatarColor}
        />
        <Message.Bubble withDriver={data.isFromDriver}>
          <Message.Header
            author={data.authorLabel}
            showAuthor={!grouped}
            timestamp={new Date(message.timestamp)}
          />
          {message.retracted ? (
            <p className="py-2 italic text-muted-foreground">
              {t("app.search.deleted")}
            </p>
          ) : (
            <>
              {message.replyMessage && (
                <div className="my-2 border-l-2 border-muted-foreground/40 pl-2 text-muted-foreground">
                  <MessageText content={message.replyMessage.body} />
                </div>
              )}
              {message.forwarded ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    {t("app.search.forwarded")}
                  </p>
                  <MessageBody forwarded content={message.forwarded.body} />
                </>
              ) : (
                <MessageBody
                  content={data.displayBody}
                  contentOffset={data.displayBodyOffset}
                />
              )}
              {message.translation && (
                <MessageText
                  content={message.translation}
                  className="mt-2 text-violet-600"
                />
              )}
              {message.reactions.length > 0 && (
                <Message.Reactions readOnly reactions={data.reactions} />
              )}
            </>
          )}
        </Message.Bubble>
      </Message>
    </div>
  )
}

export default memo(SearchHistoryMessage)
